import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getAuthHeaders } from '../utils/auth';
import axios from 'axios';

export interface PendingInvitation {
   id: string;
   type: 'trip' | 'event' | 'network' | 'album';
   title: string;
   inviterName: string;
   inviterEmail?: string;
   inviterPhotoURL?: string;
   createdAt: Date;
   expiresAt?: Date;
   customMessage?: string;
   // Type-specific data
   tripId?: string;
   eventId?: string;
   networkRequestId?: string;
   albumId?: string;
   // Additional metadata
   role?: string;
   relationshipType?: string;
   eventDate?: Date;
   tripStartDate?: Date;
   tripEndDate?: Date;
}

/**
 * Fetch all pending invitations for a user across all types
 * ONLY RETURNS INVITATIONS WHERE THE USER IS THE RECIPIENT (invited TO)
 */
export const getAllPendingInvitations = async (
   userEmail: string,
   userId: string
): Promise<PendingInvitation[]> => {
   try {
      console.log('🔍 Fetching pending invitations for user:', { userEmail, userId });
      const allInvitations: PendingInvitation[] = [];

      // Fetch trip invitations (where user is recipient)
      const tripInvitations = await getTripInvitations(userEmail);
      console.log('📧 Trip invitations received:', tripInvitations.length);
      allInvitations.push(...tripInvitations);

      // Fetch event invitations (where user is recipient)
      const eventInvitations = await getEventInvitations(userEmail);
      console.log('🎉 Event invitations received:', eventInvitations.length);
      allInvitations.push(...eventInvitations);

      // Fetch network invitations (where user is recipient)
      const networkInvitations = await getNetworkInvitations(userId);
      console.log('👥 Network invitations received:', networkInvitations.length);
      allInvitations.push(...networkInvitations);

      // Fetch album invitations (where user is recipient)
      const albumInvitations = await getAlbumInvitations(userEmail);
      console.log('📷 Album invitations received:', albumInvitations.length);
      allInvitations.push(...albumInvitations);

      console.log('✅ Total pending invitations received:', allInvitations.length);

      // Sort by creation date (newest first)
      return allInvitations.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
   } catch (error) {
      console.error('Error fetching all pending invitations:', error);
      throw new Error('Failed to load pending invitations');
   }
};

/**
 * Fetch pending trip invitations WHERE THE USER IS THE RECIPIENT
 */
const getTripInvitations = async (userEmail: string): Promise<PendingInvitation[]> => {
   try {
      // Query for invitations where the current user is the recipient (invited TO)
      const invitationsQuery = query(
         collection(db, 'tripInvitations'),
         where('email', '==', userEmail.toLowerCase()),
         where('status', '==', 'pending')
      );

      const snapshot = await getDocs(invitationsQuery);
      const invitations: PendingInvitation[] = [];

      snapshot.forEach((doc) => {
         const data = doc.data();
         // Only include invitations where the current user is the recipient
         // (email field contains the recipient's email, inviterId is the sender)
         invitations.push({
            id: doc.id,
            type: 'trip',
            title: data.tripTitle || 'Untitled Trip',
            inviterName: data.inviterName || 'Unknown',
            inviterEmail: data.inviterEmail,
            inviterPhotoURL: data.inviterPhotoURL,
            createdAt: data.createdAt?.toDate() || new Date(),
            expiresAt: data.expiresAt?.toDate(),
            customMessage: data.customMessage,
            tripId: data.tripId,
            role: data.role,
            tripStartDate: data.tripStartDate?.toDate(),
            tripEndDate: data.tripEndDate?.toDate(),
         });
      });

      return invitations;
   } catch (error) {
      console.error('Error fetching trip invitations:', error);
      return [];
   }
};

/**
 * Fetch pending event invitations WHERE THE USER IS THE RECIPIENT
 */
const getEventInvitations = async (userEmail: string): Promise<PendingInvitation[]> => {
   try {
      // Query for invitations where the current user is the recipient (invited TO)
      const invitationsQuery = query(
         collection(db, 'eventInvitations'),
         where('email', '==', userEmail.toLowerCase()),
         where('status', '==', 'pending')
      );

      const snapshot = await getDocs(invitationsQuery);
      const invitations: PendingInvitation[] = [];

      snapshot.forEach((doc) => {
         const data = doc.data();
         // Only include invitations where the current user is the recipient
         // (email field contains the recipient's email, inviterId is the sender)
         invitations.push({
            id: doc.id,
            type: 'event',
            title: data.eventTitle || 'Untitled Event',
            inviterName: data.inviterName || 'Unknown',
            inviterEmail: data.inviterEmail,
            inviterPhotoURL: data.inviterPhotoURL,
            createdAt: data.createdAt?.toDate() || new Date(),
            expiresAt: data.expiresAt?.toDate(),
            customMessage: data.customMessage,
            eventId: data.eventId,
            role: data.role,
            eventDate: data.eventDate?.toDate(),
         });
      });

      return invitations;
   } catch (error) {
      console.error('Error fetching event invitations:', error);
      return [];
   }
};

/**
 * Fetch pending network invitations
 */
const getNetworkInvitations = async (userId: string): Promise<PendingInvitation[]> => {
   try {
      const headers = await getAuthHeaders();
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/network/invitations/${userId}`;

      const response = await axios.get(apiUrl, { headers });

      if (response.data.success) {
         // Filter for received pending invitations only
         const receivedInvitations = response.data.invitations.filter(
            (inv: any) => inv.direction === 'received' && inv.status === 'pending'
         );

         return receivedInvitations.map((inv: any) => ({
            id: inv.id,
            type: 'network' as const,
            title: `${inv.relationType || 'Connection'} Request`,
            inviterName: inv.fromUserName || 'Unknown',
            inviterEmail: inv.fromUserEmail,
            inviterPhotoURL: inv.fromUserPhotoURL,
            createdAt: new Date(inv.createdAt),
            customMessage: inv.message,
            networkRequestId: inv.id,
            relationshipType: inv.relationType,
         }));
      }

      return [];
   } catch (error) {
      console.error('Error fetching network invitations:', error);
      return [];
   }
};

/**
 * Fetch pending album invitations WHERE THE USER IS THE RECIPIENT
 */
const getAlbumInvitations = async (userEmail: string): Promise<PendingInvitation[]> => {
   try {
      // Query for invitations where the current user is the recipient (invited TO)
      const invitationsQuery = query(
         collection(db, 'albumInvitations'),
         where('email', '==', userEmail.toLowerCase()),
         where('status', '==', 'pending')
      );

      const snapshot = await getDocs(invitationsQuery);
      const invitations: PendingInvitation[] = [];

      snapshot.forEach((doc) => {
         const data = doc.data();
         // Only include invitations where the current user is the recipient
         // (email field contains the recipient's email, inviterId is the sender)
         invitations.push({
            id: doc.id,
            type: 'album',
            title: data.albumTitle || 'Untitled Album',
            inviterName: data.inviterName || 'Unknown',
            inviterEmail: data.inviterEmail,
            inviterPhotoURL: data.inviterPhotoURL,
            createdAt: data.createdAt?.toDate() || new Date(),
            expiresAt: data.expiresAt?.toDate(),
            customMessage: data.customMessage,
            albumId: data.albumId,
            role: data.role,
         });
      });

      return invitations;
   } catch (error) {
      console.error('Error fetching album invitations:', error);
      return [];
   }
};

/**
 * Accept a pending invitation
 */
export const acceptInvitation = async (invitation: PendingInvitation): Promise<boolean> => {
   try {
      const headers = await getAuthHeaders();

      switch (invitation.type) {
         case 'trip':
            const tripResponse = await axios.post(
               `${process.env.REACT_APP_API_URL}/api/trip/accept-invite`,
               {
                  invitationId: invitation.id,
                  tripId: invitation.tripId,
               },
               { headers }
            );
            return tripResponse.data.success;

         case 'event':
            const eventResponse = await axios.post(
               `${process.env.REACT_APP_API_URL}/api/event/accept-invite`,
               {
                  invitationId: invitation.id,
                  eventId: invitation.eventId,
               },
               { headers }
            );
            return eventResponse.data.success;

         case 'network':
            const networkResponse = await axios.post(
               `${process.env.REACT_APP_API_URL}/api/network/accept-request`,
               {
                  requestId: invitation.networkRequestId,
               },
               { headers }
            );
            return networkResponse.data.success;

         case 'album':
            const albumResponse = await axios.post(
               `${process.env.REACT_APP_API_URL}/api/album/accept-invite`,
               {
                  invitationId: invitation.id,
                  albumId: invitation.albumId,
               },
               { headers }
            );
            return albumResponse.data.success;

         default:
            throw new Error('Unknown invitation type');
      }
   } catch (error) {
      console.error('Error accepting invitation:', error);
      return false;
   }
};

/**
 * Decline a pending invitation
 */
export const declineInvitation = async (invitation: PendingInvitation): Promise<boolean> => {
   try {
      const headers = await getAuthHeaders();

      switch (invitation.type) {
         case 'trip':
            const tripResponse = await axios.post(
               `${process.env.REACT_APP_API_URL}/api/trip/decline-invite`,
               {
                  invitationId: invitation.id,
                  tripId: invitation.tripId,
               },
               { headers }
            );
            return tripResponse.data.success;

         case 'event':
            const eventResponse = await axios.post(
               `${process.env.REACT_APP_API_URL}/api/event/decline-invite`,
               {
                  invitationId: invitation.id,
                  eventId: invitation.eventId,
               },
               { headers }
            );
            return eventResponse.data.success;

         case 'network':
            const networkResponse = await axios.post(
               `${process.env.REACT_APP_API_URL}/api/network/decline-request`,
               {
                  requestId: invitation.networkRequestId,
               },
               { headers }
            );
            return networkResponse.data.success;

         default:
            throw new Error('Unknown invitation type');
      }
   } catch (error) {
      console.error('Error declining invitation:', error);
      return false;
   }
};
